// Converte stringa durata Teams in secondi
export function durataStringaInSecondi(stringa) {
  if (!stringa) return 0;
  let totale = 0;
  const ore = stringa.match(/(\d+)h/);
  const minuti = stringa.match(/(\d+)m/);
  const secondi = stringa.match(/(\d+)s/);
  if (ore) totale += parseInt(ore[1]) * 3600;
  if (minuti) totale += parseInt(minuti[1]) * 60;
  if (secondi) totale += parseInt(secondi[1]);
  return totale;
}

export function secondiInMinuti(secondi) {
  return Math.round(secondi / 60);
}

export function minutiInStringa(minuti) {
  if (!minuti || minuti < 1) return "< 1 min";
  const h = Math.floor(minuti / 60);
  const m = Math.round(minuti % 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

export function secondiInHHMMSS(secondi) {
  const h = Math.floor(secondi / 3600);
  const m = Math.floor((secondi % 3600) / 60);
  const s = secondi % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Su alcuni eventi storici/migrati i campi data/ora non mancano ma contengono la stringa
// letterale "undefined" (bug di una serializzazione precedente): va trattata come assente.
function pulito(v) {
  return v && v !== "undefined" && v !== "null" ? v : null;
}

// Durata di riferimento di un evento in secondi (base della soglia 1/3 sulle presenze):
// la durata effettiva inserita a mano in Report presenze (evento.durataEffettivaMinuti)
// se valorizzata, altrimenti quella programmata da inizio/fine.
export function calcolaDurataEventoSecondi(evento) {
  const effettiva = Number(evento?.durataEffettivaMinuti);
  if (effettiva > 0) return Math.round(effettiva * 60);
  return calcolaDurataProgrammataSecondi(evento);
}

// Durata programmata di un evento in secondi, da dataInizio/oraInizio/dataFine/oraFine
export function calcolaDurataProgrammataSecondi(evento) {
  try {
    const dataInizio = pulito(evento.dataInizio) || pulito(evento.data);
    const oraInizio = pulito(evento.oraInizio) || "00:00";
    const dataFine = pulito(evento.dataFine) || pulito(evento.dataInizio) || pulito(evento.data);
    const oraFine = pulito(evento.oraFine) || "00:00";
    if (!dataInizio || !oraFine) return null;
    const inizio = new Date(`${dataInizio}T${oraInizio}`);
    const fine = new Date(`${dataFine}T${oraFine}`);
    const diff = Math.round((fine - inizio) / 1000);
    return diff > 0 ? diff : null;
  } catch {
    return null;
  }
}

function normalizzaNome(nome) {
  if (!nome) return "";
  return nome.replace(/\(.*?\)/gi, "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function pulisciNome(nome) {
  if (!nome) return "";
  const pulito = nome.replace(/\(.*?\)/gi, "").trim();
  return pulito.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// Split euristico "Nome Cognome" -> { nome, cognome }: primo token è il nome, il resto
// il cognome. Stessa euristica usata come fallback in matchIscrittiPresenza (parseReportIF.js):
// va usata solo per i partecipanti non abbinati al file iscritti (vedi utils/iscritti.js).
export function dividiNomeCognome(nomeCompleto) {
  const parti = (nomeCompleto || "").trim().split(/\s+/).filter(Boolean);
  return {
    nome: parti[0] || "",
    cognome: parti.slice(1).join(" "),
  };
}

// Nome/cognome di un partecipante salvato in reportPresenze: dal file iscritti se abbinato
// (vedi utils/iscritti.js), altrimenti split euristico del nome Teams (daVerificare: true)
export function nomeCognomePartecipante(p) {
  if (p.abbinatoIscritti) return { nome: p.nomeIscritto || "", cognome: p.cognomeIscritto || "", daVerificare: false };
  return { ...dividiNomeCognome(p.nome), daVerificare: true };
}

function isNomeGenerico(nome) {
  if (!nome) return true;
  const pulito = nome.replace(/\(.*?\)/gi, "").trim();
  const tokens = pulito.split(/\s+/).filter(Boolean);
  if (tokens.length <= 1) return true;
  if (pulito.length <= 2) return true;
  return false;
}

const PAROLE_BOT = ["virtual meeting assistant", "managing", "meeting recorder"];

function isBot(nome) {
  if (!nome) return false;
  const n = nome.toLowerCase();
  return PAROLE_BOT.some((bot) => n.includes(bot));
}

function rilevaFormato(righe) {
  const primaRiga = righe[0]?.join("\t") || "";
  if (primaRiga.replace(/^\uFEFF/, "").startsWith("Nome\t")) {
    return "webinar";
  }
  return "meeting";
}

// Trova la sezione "3. Attività di riunione"
function trovaHeaderAttivita(righe) {
  for (let i = 0; i < righe.length; i++) {
    const riga = righe[i];
    if (riga[0]?.includes("3. Attività di riunione") || riga[0]?.includes("3. Meeting activity")) {
      // L'header è nella riga successiva
      if (i + 1 < righe.length) return i + 1;
    }
  }
  return -1;
}

// Trova la sezione "2. Partecipanti" (fallback)
function trovaHeaderPartecipanti(righe) {
  for (let i = 0; i < righe.length; i++) {
    const riga = righe[i];
    if (
      (riga[0] === "Nome" || riga[0]?.replace(/^\uFEFF/, "") === "Nome") &&
      riga.some((c) => c === "Durata riunione")
    ) {
      return i;
    }
  }
  return -1;
}

function leggiDurataMaxRiunione(righe) {
  for (let i = 0; i < Math.min(righe.length, 10); i++) {
    if (righe[i][0] === "Durata della riunione") {
      const durata = durataStringaInSecondi(righe[i][1]);
      if (durata > 0) return durata;
    }
  }
  return null;
}

// Parsing dalla sezione "3. Attività di riunione" — somma colonna Durata per partecipante
function parseAttivita(righe, headerIdx, durataMaxSecondi) {
  const header = righe[headerIdx];
  const colNome = header.findIndex((c) => c === "Nome");
  const colEmail = header.findIndex((c) => c === "Posta elettronica");
  const colDurata = header.findIndex((c) => c === "Durata");

  if (colNome < 0 || colDurata < 0) return null;

  const partecipanti = {};

  for (let i = headerIdx + 1; i < righe.length; i++) {
    const riga = righe[i];
    if (!riga || riga.length < 3 || !riga[colNome]) break;

    const nome = riga[colNome]?.trim() || "";
    if (!nome || nome === "Nome") continue;
    if (isBot(nome)) continue;

    const email = riga[colEmail]?.trim()?.toLowerCase() || "";
    if (!email && isNomeGenerico(nome)) continue;

    const durataSecondi = durataStringaInSecondi(riga[colDurata]);
    const chiave = email || normalizzaNome(nome);
    if (!chiave) continue;

    if (partecipanti[chiave]) {
      partecipanti[chiave].secondiTotali += durataSecondi;
      if (nome.length > partecipanti[chiave]._nomeOriginale.length) {
        partecipanti[chiave]._nomeOriginale = nome;
      }
      if (!partecipanti[chiave].email && email) {
        partecipanti[chiave].email = email;
      }
    } else {
      partecipanti[chiave] = {
        _nomeOriginale: nome,
        email,
        secondiTotali: durataSecondi,
      };
    }
  }

  return Object.values(partecipanti).map((p) => {
    const secondiCappati = durataMaxSecondi
      ? Math.min(p.secondiTotali, durataMaxSecondi)
      : p.secondiTotali;
    return {
      nome: pulisciNome(p._nomeOriginale),
      email: p.email,
      durataMinuti: secondiInMinuti(secondiCappati),
      durataSecondi: secondiCappati,
    };
  });
}

// Parsing dalla sezione "2. Partecipanti" (fallback per webinar o file senza sezione 3)
function aggregaPartecipanti(righe, headerIdx, colNome, colEmail, colDurata, colEmailReg, durataMaxSecondi) {
  const partecipanti = {};

  for (let i = headerIdx + 1; i < righe.length; i++) {
    const riga = righe[i];
    if (!riga || riga.length < 3) continue;

    const nome = riga[colNome]?.trim() || "";
    if (!nome || nome === "Nome") continue;
    if (isBot(nome)) continue;

    const emailPrincipale = riga[colEmail]?.trim()?.toLowerCase() || "";
    const emailRegistrazione = colEmailReg >= 0
      ? riga[colEmailReg]?.trim()?.toLowerCase() || ""
      : "";
    const email = emailPrincipale || emailRegistrazione;

    if (!email && isNomeGenerico(nome)) continue;

    const durataSecondi = durataStringaInSecondi(riga[colDurata]);
    const chiave = email || normalizzaNome(nome);
    if (!chiave) continue;

    if (partecipanti[chiave]) {
      partecipanti[chiave].secondiTotali += durataSecondi;
      if (nome.length > partecipanti[chiave]._nomeOriginale.length) {
        partecipanti[chiave]._nomeOriginale = nome;
      }
      if (!partecipanti[chiave].email && email) {
        partecipanti[chiave].email = email;
      }
    } else {
      partecipanti[chiave] = {
        _nomeOriginale: nome,
        email,
        secondiTotali: durataSecondi,
      };
    }
  }

  return Object.values(partecipanti).map((p) => {
    const secondiCappati = durataMaxSecondi
      ? Math.min(p.secondiTotali, durataMaxSecondi)
      : p.secondiTotali;
    return {
      nome: pulisciNome(p._nomeOriginale),
      email: p.email,
      durataMinuti: secondiInMinuti(secondiCappati),
      durataSecondi: secondiCappati,
    };
  });
}

function parseMeeting(righe, durataMaxSecondi) {
  // Prima prova sezione 3
  const headerAttivita = trovaHeaderAttivita(righe);
  if (headerAttivita >= 0) {
    const risultato = parseAttivita(righe, headerAttivita, durataMaxSecondi);
    if (risultato && risultato.length > 0) return risultato;
  }

  // Fallback: sezione 2
  const headerIdx = trovaHeaderPartecipanti(righe);
  if (headerIdx < 0) return [];
  const header = righe[headerIdx];
  const colNome = header.findIndex((c) => c === "Nome");
  const colEmail = header.findIndex((c) => c === "Posta elettronica");
  const colDurata = header.findIndex((c) => c === "Durata riunione");
  return aggregaPartecipanti(righe, headerIdx, colNome, colEmail, colDurata, -1, durataMaxSecondi);
}

function parseWebinar(righe, durataMaxSecondi) {
  const headerIdx = trovaHeaderPartecipanti(righe);
  if (headerIdx < 0) return [];
  const header = righe[headerIdx];
  const colNome = header.findIndex((c) => c === "Nome" || c?.replace(/^\uFEFF/, "") === "Nome");
  const colEmail = header.findIndex((c) => c === "Posta elettronica");
  const colDurata = header.findIndex((c) => c === "Durata riunione");
  const colEmailReg = header.findIndex((c) => c === "Indirizzo di posta elettronica di registrazione");
  return aggregaPartecipanti(righe, headerIdx, colNome, colEmail, colDurata, colEmailReg, durataMaxSecondi);
}

export async function parseReportPresenze(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const buffer = e.target.result;
        const decoder = new TextDecoder("utf-16le");
        let testo = decoder.decode(buffer);

        if (testo.charCodeAt(0) === 0xFEFF) {
          testo = testo.slice(1);
        }

        const righe = testo
          .split(/\r?\n/)
          .map((r) => r.split("\t"));

        const formato = rilevaFormato(righe);
        const durataMaxSecondi = formato === "meeting" ? leggiDurataMaxRiunione(righe) : null;

        let partecipanti;
        if (formato === "webinar") {
          partecipanti = parseWebinar(righe, durataMaxSecondi);
        } else {
          partecipanti = parseMeeting(righe, durataMaxSecondi);
        }

        partecipanti.sort((a, b) => b.durataSecondi - a.durataSecondi);

        const totalePartecipanti = partecipanti.length;
        const durataMedia = totalePartecipanti > 0
          ? Math.round(partecipanti.reduce((acc, p) => acc + p.durataMinuti, 0) / totalePartecipanti)
          : 0;

        resolve({
          partecipanti,
          totalePartecipanti,
          durataMedia,
          formato,
          durataMaxSecondi,
        });
      } catch (err) {
        console.error("Errore parsing:", err);
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}